import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client'
import SingleLineEllipsis from '@deltek/specification-client/core/Feedbacks/SingleLineEllipsis'
import { CurrentUserActiveFirmQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserActiveFirmResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    FirmId,
    FirmResponse,
} from '@deltek/specification-client/core/Models/firm'
import { ListingDetail } from '@deltek/specification-client/core/Models/listing'
import {
    ListingLevel,
    ManufacturerProductSpecDocDetail,
} from '@deltek/specification-client/core/Models/product'
import { User } from '@deltek/specification-client/core/Models/user'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    getNormalizedProperties,
    getSelectedProperties,
} from '@deltek/specification-client/core/Widgets/ProductListing/Details/productDetailUtils'
import { PropertyDetailsViewMode } from '@deltek/specification-client/core/Widgets/PropertyDetailsViewMode/PropertyDetailsViewMode'
import { Toolbar } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import Skeleton from '@material-ui/lab/Skeleton'
import { FirmSupportContactUserQuery } from 'api/graphQL/queries/firm.queries'
import { ProductListingDetailsQuery } from 'api/graphQL/queries/product.queries'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { KeyboardEventHandler, useCallback } from 'react'
import Truncate from 'react-truncate'
import ProductListingDialogSubmissionStatus from './Card/ProductListingDialogSubmissionStatus'
import { DialogContext } from './DialogContext'
import ListingDetailMediaCarousel from './Media/ListingDetailMediaCarousel'
import FirmContacts from './Resources/FirmContacts'
import { ViewDescription } from './Resources/ViewDescription'
import { ViewDocuments } from './Resources/ViewDocuments'
import { ViewLinks } from './Resources/ViewLinks'
import ViewPartnerships from './Resources/ViewPartnerShips'
import { SlideContentTabs } from './Tabs/SlideContentTabs'

const useStyles = makeStyles((theme: Theme) => ({
    resourceTabBody: {
        padding: theme.spacing(3),
    },
    grow: {
        flexGrow: 1,
    },
    headerCloseIconButton: {
        paddingLeft: 0,
        paddingRight: 0,
    },
    headerText: {
        ...theme.typography.h1,
        color: '#FFFFFF',
    },
    slideHeader: {
        backgroundColor: '#A78966',
    },
    slideHeaderToolbar: {
        minHeight: 50,
        paddingRight: theme.spacing(2),
    },
    firmLogoImage: {
        height: 50,
        objectFit: 'contain',
        display: 'block',
        maxWidth: '100%',
    },
    logoContainer: {
        margin: theme.spacing(2, 0),
        padding: theme.spacing(2, 3),
        display: 'flex',
        flexDirection: 'row',
        paddingRight: 0,
        alignItems: 'center',
    },

    tabContentContainer: {
        marginLeft: theme.spacing(0.5),
    },
    slideContainer: {
        height: '100%',
        backgroundColor: '#f1f1f1',
    },
    propertiesContainer: {
        padding: theme.spacing(3),
        paddingTop: 0,
        [theme.breakpoints.down('md')]: {
            padding: theme.spacing(1),
        },
    },
    slideBody: {
        paddingBottom: theme.spacing(2),
    },
    basicListing: { backgroundColor: '#343334' },
    header: {
        minHeight: 108,
        color: theme.palette.secondary.light,
        marginLeft: theme.spacing(-3),
        width: 'calc(100% + 48px)',
    },
    imageGalleryFlexContainer: {
        [theme.breakpoints.down('sm')]: {
            borderBottom: '1px solid #c9c9c9',
        },
    },
    basicListContainer: {
        display: 'flex',
        height: 400,
        color: theme.palette.secondary.light,
        padding: theme.spacing(2),
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: pxToRem(72),
        [theme.breakpoints.down('sm')]: { marginRight: 0 },
        textAlign: 'center',
    },
}))

export const filterInactiveUsers = (users: Partial<User>[] = []) =>
    users.filter((item) => {
        return (
            item.role?.toLowerCase() !== 'onboarder' &&
            item.status?.toLowerCase() !== 'inactive'
        )
    })

export interface SlideContentProps {
    searchLoading?: boolean
    projectId?: string
}
const getDocDetail = (
    docs: Partial<ManufacturerProductSpecDocDetail>[] | undefined,
    docType: 'ShortForm' | 'FullLength'
): Partial<ManufacturerProductSpecDocDetail> | undefined =>
    docs?.filter((each) => each.docType === docType && !!each.path)?.[0]

export interface SlideContentProps {
    listingId?: string
}

export const SlideContent: React.FC<SlideContentProps> = ({
    searchLoading,
    projectId,
}) => {
    const {
        handleFirmLogo,
        handleSubmissionStatus,
        firmLogoUrl,
        activeTab,
        listingId,
        handleSlideOpen,
        handleListingId,
        handleTabChange,
    } = React.useContext(DialogContext)
    const [fullView, setFullView] = React.useState(true)

    const { currentUserInfo } = useReactiveVar(userInfo)

    const handleChange = () => {
        setFullView(!fullView)
    }
    const [
        getFirmResponse,
        { data: firmSupportContactData, loading: firmSupportContactLoading },
    ] = useLazyQuery<FirmResponse, FirmId>(FirmSupportContactUserQuery)

    const { data: currentUserActiveFirmResponse } = useQuery<
        CurrentUserActiveFirmResponse
    >(CurrentUserActiveFirmQuery)

    const classes = useStyles()

    const { data, loading } = useQuery<{
        getListingDetail: Partial<ListingDetail>
    }>(ProductListingDetailsQuery, {
        variables: {
            listingIdInput: {
                listingId,
                projectId,
                logEventSource: 'AeListingDetail',
            },
        },
        fetchPolicy: 'cache-and-network',
        onCompleted: (data) => {
            handleTabChange?.(
                data.getListingDetail.type === 'Manufacturer' ||
                    data.getListingDetail.type ===
                        ListingLevel.PremiumProprietary
                    ? 'resources'
                    : 'properties'
            )
            getFirmResponse({
                variables: {
                    firmId: data?.getListingDetail?.firmId ?? '',
                },
            })
            handleFirmLogo?.(data.getListingDetail?.firmLogoUrl)
            handleSubmissionStatus?.(data.getListingDetail?.submissionStatus)
        },
    })
    const firmDisplayName = React.useMemo(
        () => data?.getListingDetail?.firmDisplayName,
        [data]
    )
    const isBasic = React.useMemo(
        () => data?.getListingDetail?.type === ListingLevel.Manufacturer,
        [data]
    )
    const defaultContacts: Partial<User>[] = React.useMemo(() => {
        return (
            (filterInactiveUsers(data?.getListingDetail?.assignedContactUsers)
                .length &&
                filterInactiveUsers(
                    data?.getListingDetail?.assignedContactUsers
                )) ||
            (firmSupportContactData?.firm?.designatedAccountAdminUser
                ? [firmSupportContactData?.firm?.designatedAccountAdminUser]
                : ([] as Partial<User>[]))
        )
    }, [data, firmSupportContactData])

    const handleSlideClose = () => {
        handleSlideOpen?.(false)
        handleListingId?.(undefined)
        handleFirmLogo?.(undefined)
        handleSubmissionStatus?.(undefined)
    }
    const handleSlideCloseClick = () => {
        handleSlideClose?.()
    }

    const handleEscape: KeyboardEventHandler<HTMLDivElement> = useCallback(
        (e) => {
            if (e.key === 'Escape') {
                handleSlideClose?.()
            }
        },
        []
    )

    return (
        <div className={classes.slideContainer} onKeyDown={handleEscape}>
            <div className={classes.slideHeader}>
                <Toolbar className={classes.slideHeaderToolbar}>
                    <Typography
                        variant="h1"
                        noWrap
                        className={classes.headerText}
                    >
                        {searchLoading && loading ? (
                            <Skeleton
                                animation="pulse"
                                width={200}
                                className="loadingSkeleton"
                            />
                        ) : (
                            <SingleLineEllipsis
                                className={classes.headerText}
                                text={data?.getListingDetail.productName ?? ''}
                            />
                        )}
                    </Typography>
                    <div className={classes.grow} />
                    <IconButton
                        className={clsx(
                            'slideContentClose',
                            classes.headerCloseIconButton
                        )}
                        onClick={handleSlideCloseClick}
                        aria-label="Close Slide Content"
                    >
                        <CloseIcon htmlColor="#FFFFFF" />
                    </IconButton>
                </Toolbar>
            </div>
            <div className={classes.slideBody}>
                {searchLoading && loading ? (
                    <Skeleton
                        animation="pulse"
                        width={200}
                        className="loadingSkeleton"
                    />
                ) : (
                    <>
                        {isBasic ? (
                            <GridItem
                                className={clsx(
                                    classes.basicListing,
                                    classes.basicListContainer
                                )}
                                data-testid="basicDetailContainer"
                            >
                                Manufacturer Listing
                            </GridItem>
                        ) : (
                            <ListingDetailMediaCarousel
                                images={data?.getListingDetail?.images}
                                videos={data?.getListingDetail?.videos}
                            />
                        )}
                        <SlideContentTabs type={data?.getListingDetail.type} />

                        <GridItem
                            container
                            justifyContent="space-between"
                            className={classes.logoContainer}
                            data-testid="enhancedPremiumDetailContainer"
                        >
                            {firmLogoUrl ? (
                                <img
                                    src={firmLogoUrl}
                                    className={classes.firmLogoImage}
                                    alt={'firmName'}
                                    title={firmDisplayName}
                                    data-testid="companyName"
                                />
                            ) : (
                                <Truncate
                                    title={firmDisplayName}
                                    lines={2}
                                    data-testid="companyName"
                                >
                                    {firmDisplayName}
                                </Truncate>
                            )}
                            {processEnv.REACT_APP_EnableBadgelessCards !==
                                '1' && (
                                <ProductListingDialogSubmissionStatus
                                    loading={loading}
                                    variant={data?.getListingDetail?.type}
                                />
                            )}
                        </GridItem>
                        <GridItem>
                            {!!activeTab &&
                            activeTab === 'properties' &&
                            data?.getListingDetail?.type !==
                                ListingLevel.PremiumProprietary ? (
                                !!data?.getListingDetail?.properties && (
                                    <GridItem
                                        className={classes.propertiesContainer}
                                    >
                                        <PropertyDetailsViewMode
                                            level={data?.getListingDetail?.type}
                                            toggleColor="#304959"
                                            onChange={handleChange}
                                            isFullView={fullView}
                                            properties={getNormalizedProperties(
                                                data?.getListingDetail
                                                    ?.properties
                                            )}
                                            selectedData={getSelectedProperties(
                                                data?.getListingDetail
                                                    ?.properties
                                            )}
                                        />{' '}
                                    </GridItem>
                                )
                            ) : (
                                <div className={classes.resourceTabBody}>
                                    {!isBasic &&
                                        data?.getListingDetail
                                            ?.longDescription && (
                                            <ViewDescription
                                                longDescription={
                                                    data?.getListingDetail
                                                        ?.longDescription
                                                }
                                            />
                                        )}
                                    {!isBasic && data?.getListingDetail && (
                                        <FirmContacts
                                            productName={
                                                data?.getListingDetail
                                                    ?.productName ?? ''
                                            }
                                            activeFirmName={
                                                currentUserActiveFirmResponse
                                                    ?.myActiveFirm?.firmName
                                            }
                                            assignedContactUsers={
                                                defaultContacts
                                            }
                                            currentUserInfo={currentUserInfo}
                                        />
                                    )}
                                    <ViewDocuments
                                        level={data?.getListingDetail?.type}
                                        catalog={
                                            data?.getListingDetail
                                                ?.catalogs?.[0]
                                        }
                                        longDocType={getDocDetail(
                                            data?.getListingDetail
                                                ?.manufacturerDocs,
                                            'FullLength'
                                        )}
                                        shortDocType={getDocDetail(
                                            data?.getListingDetail
                                                ?.manufacturerDocs,
                                            'ShortForm'
                                        )}
                                    />
                                    {!(
                                        !data?.getListingDetail?.type ||
                                        !data?.getListingDetail?.webLinks ||
                                        data?.getListingDetail?.type ===
                                            ListingLevel.Manufacturer ||
                                        data?.getListingDetail?.webLinks
                                            ?.length === 0 ||
                                        data?.getListingDetail?.webLinks.filter(
                                            (each) =>
                                                each.url !== '' &&
                                                each.url !== 'http://'
                                        ).length === 0
                                    ) && (
                                        <ViewLinks
                                            level={data?.getListingDetail?.type}
                                            webLinks={
                                                data?.getListingDetail?.webLinks
                                            }
                                        />
                                    )}
                                    <ViewPartnerships
                                        level={data?.getListingDetail?.type}
                                        partnerships={
                                            data?.getListingDetail
                                                ?.partnershipLinks
                                        }
                                    />
                                </div>
                            )}
                        </GridItem>
                    </>
                )}
            </div>
        </div>
    )
}
